import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ReferralCardSvg, loadingGif } from '../../../assets/images';
import { useConfig } from '../../../hooks/use-config';
import { postReferralAgreement } from '../../../services/agreement';
import { Button } from '../../../stories/Button';

interface ReferralConfirmationProps {
  onSubmit: () => void;
  isError: boolean;
}

export const ReferralConfirmation: React.FC<ReferralConfirmationProps> = ({
  onSubmit,
  isError,
}) => {
  const __ = useTranslation().t;
  const { config } = useConfig();

  const [isChecked, setIsChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      await postReferralAgreement({ over_18years_agreement: isChecked });
      onSubmit();
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      alert(__('whopsSomethingWentWrong'));
    }
  };

  return (
    <Container>
      <Title>{__('referral.title')}</Title>

      <Message>
        <Trans alt={__('referral.message')} i18nKey="referral.message">
          You earn a $100 giftcard 60 days after your friend purchases the course.{' '}
          <strong>
            We must validate that you are of legal age so that you can access the referral plan.
          </strong>
        </Trans>
      </Message>

      <ReferralCardSvg />
      {!isError && (
        <CheckboxLabel>
          <input
            disabled={isLoading}
            type="checkbox"
            checked={isChecked}
            onChange={() => setIsChecked(!isChecked)}
          />{' '}
          {__('referral.confirmation')}
        </CheckboxLabel>
      )}

      {isLoading ? (
        <img src={loadingGif} alt="Loading" />
      ) : (
        !isError && (
          <Button
            id="oejr-login-submit-button"
            label={__('continue')}
            type="submit"
            disabled={!isChecked || isLoading}
            variant="primary"
            onClick={handleSubmit}
          />
        )
      )}

      <FooterMessage>
        <Trans alt={__('referral.footerMessage')} i18nKey="referral.footerMessage">
          See{' '}
          <a target="_blank" href={config.privacyPoliceExtole}>
            Privacy policy
          </a>{' '}
          for details
        </Trans>
      </FooterMessage>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 550px;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  padding: 40px 64px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 600;
  font-family: ${({ theme }) => theme.fonts.primary.regular.fontFamily};
`;

const Message = styled.p`
  font-size: 16px;
  font-weight: 400;
  font-family: ${({ theme }) => theme.fonts.secondary.regular.fontFamily};
  margin-top: 16px;
  padding-bottom: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.dark};
`;

const CheckboxLabel = styled.label`
  font-size: 16px;
  font-weight: 400;
  font-family: ${({ theme }) => theme.fonts.secondary.regular.fontFamily};
  margin: 16px 0;
  color: ${({ theme }) => theme.colors.dark};
`;

const FooterMessage = styled.p`
  font-size: 12px;
  font-weight: 400;
  font-family: ${({ theme }) => theme.fonts.secondary.regular.fontFamily};
  margin-top: 16px;
  color: ${({ theme }) => theme.colors.dark};
`;
